import * as d3 from 'd3-selection'
import graph from './graph'
import { transition } from 'd3-transition'
import { EPSILON } from 'chart-constants'
import isEqual from 'lodash.isequal'

const clipBuffer = 8.5 // so line chart points and bar borders appear if they are at the bottom/top of the chart

let isBreakdownSwap = (previous, current) => {
  if (!previous) {
    return false
  }
  let plots0 = previous.plotScale.domain()
    , plots1 = current.plotScale.domain()
    , range0 = previous.groupScale.domain()
    , range1 = current.groupScale.domain()

  return isEqual(plots0, range1) && isEqual(plots1, range0)
}

function _swapBreakdowns (plot, enter, exit, scalers, chart, domain0, domain1) {
  enter.attr('transform', d => {
    // Swapping breakdowns allows the new plots to have full control.
    // The final plot position is set at the end of the transition
    return `translate(0,0)`
  })

  chart.defaultTransition.end().then(() => {
    // Once the transition ends, set the proper location
    // for this plot. Each graph type animation has to be responsible for
    // setting the correct location within the transformed plot when the transition
    // ends!
    plot.attr('transform', plot => `translate(${scalers.x.plotScale(plot.id)},0)`)
    exit.remove()
  }).catch(() => {
    //canceled transition, ignore
  })

  // Hide all the components in the exiting plots.
  // Each animation will need to start its drawings in the proper location
  // The x-axis code will be responsible for fading out the x-axis
  exit.selectAll(':scope > *').style('opacity', EPSILON)
}

function _slide (plot, enter, exit, scalers, chart, domain0, domain1) {
  enter.attr('transform', d => {
    let x = scalers.x.plotScale(d.id) // Default to final location
    if (!chart.previous) {
     // Start in the final location
     return `translate(${x}, 0)`
    }

    let idx = scalers.x.plotScale.domain().indexOf(d.id)
     , step = chart.previous.scalers.x.plotScale.step()
     , prevStartIdx = domain1.indexOf(domain0[0])
     , prevEndIdx = domain1.indexOf(domain0[domain0.length - 1])

    if (idx < prevStartIdx) {
     // Coming in from the left
     x = chart.previous.scalers.x.plotScale(domain0[0]) - step * (prevStartIdx - idx)
    } else if (idx > prevEndIdx) {
     // Coming in from the right
     x = chart.previous.scalers.x.plotScale(domain0[domain0.length - 1]) + step * (idx - prevEndIdx)
    }
    return `translate(${x},0)`
  })

  plot.transition(chart.defaultTransition)
      .attr('transform', plot => `translate(${scalers.x.plotScale(plot.id)},0)`)

  exit.transition(chart.defaultTransition)
      .attr('transform', d => {
       let idx = domain0.indexOf(d.id)
         , prevStart = domain0.indexOf(domain1[0])
         , prevEnd = domain0.indexOf(domain1[domain1.length - 1])
         , step = scalers.x.plotScale.step()
         , x = chart.previous.scalers.x.plotScale(d.id)

       if (prevStart == -1 && prevEnd == -1) {
         // Total swap, slide out to the left
         x = scalers.x.plotScale(domain1[0]) - step
       } else if (idx < prevStart) {
         // out to the left
         x = scalers.x.plotScale(domain1[0]) - step * (prevStart - idx)
       } else if (idx > prevEnd) {
         // out to the right
         x = scalers.x.plotScale(domain1[domain1.length - 1]) + step * (idx - prevEnd)
       }

       return `translate(${x}, 0)`
     })
    .remove()
}

function position (plot, enter, exit, scalers, chart, domain0, domain1) {
  if (chart.datum().swappingBreakdowns) {
    _swapBreakdowns(plot, enter, exit, scalers, chart, domain0, domain1)
  } else {
    _slide(plot, enter, exit, scalers, chart, domain0, domain1)
  }
}

function plot (selection, chart, xaxis, scalers, hover) {
  selection.each(function (data) {
    let svg = d3.select(this.ownerSVGElement)
      , chartData = chart.datum()
      , domain0 = chart.previous && chart.previous.scalers.x.plotScale.domain()
      , domain1 = scalers.x.plotScale.domain()
      , _plot = d3.select(this)
                  .selectAll('g.plot')
                  .data(d => d, plot => plot.id)
      , plotEnter = _plot.enter()
                         .append('g')
                           .attr('class', 'plot')
      , plot = plotEnter.merge(_plot)
      , width = chart.datum().swappingBreakdowns ? scalers.x.plotScale.range()[1] // full width
                                                 : scalers.x.plotScale.step()
      , plotExit = _plot.exit()
                        .classed('plot-exiting', true)

    position(plot, plotEnter, plotExit, scalers, chart, domain0, domain1)

    plotExit.call(xaxis(scalers, chart)) // Update the axis for the exiting plot in case things need to be realigned
            .select(plot => {
              // Remove the old clip path
              svg.selectAll(`defs .clippath-${plot.id}`).remove()
            })

    // Each plot needs its own clipPath in the svg defs
    plotEnter.each(plot => {
      // All plots use this clip path. It makes sure no elements extend left/right
      // of the width bounds
      svg.select('defs')
          .append('clipPath')
            .attr('class', `clippath clippath-${plot.id}`) // easy to select
            .attr('id', `${svg.attr('id')}-chart-clip-path-plot-width-${plot.id}`)
            .append('rect')
              .attr('width', width)
              .attr('x', -clipBuffer)
              .attr('height', chartData.dimensions._containerHeight) // 100%
              .attr('y', '-10') // offset

      // Add another clip path that allows full width, but restricts elements from being outside the height
      // This can be used by individual objects on the chart, but isn't applied globally
      svg.select('defs')
          .append('clipPath')
            .attr('class', `clippath clippath-${plot.id}`)
            .attr('id', `${svg.attr('id')}-chart-clip-path-plot-height-${plot.id}`)
            .append('rect')
              .attr('width', chartData.dimensions._containerWidth + clipBuffer * 2) // 100%
              .attr('x', -clipBuffer)
              .attr('height', Math.max(0, chartData.dimensions.height) + clipBuffer * 2)
              .attr('y', -clipBuffer)

      // This restricts all drawings inside of the plot
      svg.select('defs')
          .append('clipPath')
            .attr('class', `clippath clippath-${plot.id}`)
            .attr('id', `${svg.attr('id')}-chart-clip-path-plot-width-height-bufferless-${plot.id}`)
            .append('rect')
              .attr('width', chartData.dimensions._containerWidth) // 100%
              .attr('x', 0)
              .attr('height', Math.max(0, chartData.dimensions.height))
              .attr('y', 0)
    })

    // Make sure the clip path `rect` has the correct dimensions for each plot
    plot.select(plot => {
      let defs = svg.select('defs').transition(chart.defaultTransition)
        , widthClip = defs.selectAll(`.clippath#${svg.attr('id')}-chart-clip-path-plot-width-${plot.id} rect`)
        , heightClip = defs.selectAll(`.clippath#${svg.attr('id')}-chart-clip-path-plot-height-${plot.id} rect`)
        , widthHeightBufferlessClip = defs.selectAll(`.clippath#${svg.attr('id')}-chart-clip-path-plot-width-height-bufferless-${plot.id} rect`)

      widthClip.attr('width', width + clipBuffer * 2)
               .attr('height', chartData.dimensions._containerHeight) // 100%
      heightClip.attr('height', Math.max(0, chartData.dimensions.height) + clipBuffer * 2)
                .attr('width', chartData.dimensions._containerWidth) // 100%

      widthHeightBufferlessClip.attr('width', width)
                               .attr('height', Math.max(0, chartData.dimensions.height))

      defs.end().then(() => {
        // Force the plot clip path to be the bandwidth in case it was the
        // full width during a breakdown swap
        widthClip.selection().attr('width', scalers.x.plotScale.step() + clipBuffer * 2)
      }).catch(() => {
        // Don't care. Another transition started running so this was interrupted
      })
    })

    plot.call(xaxis(scalers, chart)) // Add the axis for the plot
        .call(graph, chart, scalers)
        .call(hover, {
          height: chartData.dimensions.height,
          width: scalers.x.plotScale.bandwidth()
        }, scalers, chart)
  })
}

export default function (selection, chart, xaxis, scalers, hover) {
  selection.each(function (data) {
    chart.datum().swappingBreakdowns = isBreakdownSwap(chart.previous && chart.previous.scalers.x, scalers.x)
    let plots = d3.select(this).selectAll('g.plots')
      , container = d3.select(this)
                      .selectAll('g.plots')
                      .data(d => [d.plots])
                      .enter()
                        .append('g')
                          .attr('class', 'plots')
                          .merge(plots)
                          .call(plot, chart, xaxis, scalers, hover)

    container.exit().remove()
  })
}
